import * as React from "react";
import { Folder20Regular } from "@fluentui/react-icons";
import { apiUrl } from "../../utils/api";
import { documentService } from "../../services/documentService";
import { kundenakteOrdnerService } from "../../services/kundenakteOrdnerService";
import { graphMailService } from "../../services/graphMailService";
import { KontaktSuchfeld } from "../forms/widgets/KontaktSuchfeld";
import { AkteZuweisenPopup } from "../alteKundenakten/AkteZuweisenPopup";
import { OpenWindowConfig, useWindowManager } from "../windowManager/WindowManagerContext";
import { OrdnerNavigator, ordnerTitel } from "../documents/explorer/OrdnerNavigator";
import { findeSchadenBasis, ordnerName } from "./schadenOrdner";
import { frageSchadenAnhaenge } from "./schadenAnhaenge";

/** Kategorie, unter der der Reiter „Dokumente" den Ordnerbaum der Akte führt. */
const KUNDENAKTE = "Alte Kundenakte";

export type SchadenAblageModus = "neu" | "zuordnen";

export type OpenSchadenAblageParams = {
    modus: SchadenAblageModus;
    mailbox: string;
    messageId: string;
    betreff: string | null;
    /** Erkannter Kontakt der Mail — vorbelegt, bleibt aber änderbar. */
    kontakt?: { id: number; displayName: string } | null;
    /** Die Mail liegt im Ordner — der Aufrufer archiviert sie jetzt. */
    onAbgelegt: (ergebnis: { contactId: number; ordner: string; hinweis: string | null }) => void;
};

type Kontakt = { id: number; name: string };

/**
 * Schadenpostfach (PO 05.10.2026): „Neuer Schaden" und „Einem bestehenden
 * Schaden zuordnen". Ein Schaden ist ein Ordner im Ordner „Schaden" der
 * Kundenakte des Kontakts; dorthin wandern die Mail und — nach Rückfrage
 * (User 04.11.2026) — ihre Anhänge, alle oder die gewählten.
 *
 * Ablauf: Kontakt wählen (Pflicht) → hat er keine Kundenakte, einmal fragen, ob
 * eine zugewiesen werden soll (es geht auch ohne) → durch die Ordner der Akte
 * navigieren, beliebig tief (PO 06.10.2026: unter „Schaden" liegen oft erst
 * Jahresordner, der Schaden eine Ebene darunter) → im geöffneten Ordner
 * ablegen oder dort einen neuen Schaden-Ordner anlegen.
 */
const SchadenAblageFenster: React.FC<OpenSchadenAblageParams & { windowId: string }> = ({
    windowId,
    modus,
    mailbox,
    messageId,
    betreff,
    kontakt: vorbelegt,
    onAbgelegt,
}) => {
    const { closeWindow } = useWindowManager();
    const [kontakt, setKontakt] = React.useState<Kontakt | null>(
        vorbelegt ? { id: vorbelegt.id, name: vorbelegt.displayName } : null,
    );
    const [laedt, setLaedt] = React.useState(false);
    // Geöffneter Ordner der Akte ("" = Wurzel, sonst mit „/" am Ende).
    const [pfad, setPfad] = React.useState("");
    // Kundenakte: null = noch unbekannt, sonst ob dem Kontakt eine zugewiesen ist.
    const [hatAkte, setHatAkte] = React.useState<boolean | null>(null);
    const [akteFrageBeantwortet, setAkteFrageBeantwortet] = React.useState(false);
    const [akteZuweisen, setAkteZuweisen] = React.useState(false);
    // Neuer Schaden-Ordner im geöffneten Ordner. Bei „Neuer Schaden" mit dem
    // Betreff vorbelegt; beim Zuordnen leer — dann ist der geöffnete Ordner das Ziel.
    const [neuerName, setNeuerName] = React.useState(() => (modus === "neu" ? ordnerName(betreff) : ""));
    const [busy, setBusy] = React.useState(false);
    const [fehler, setFehler] = React.useState<string | null>(null);
    const [ladeTick, setLadeTick] = React.useState(0);

    // Einstieg und Akten-Zuweisung des gewählten Kontakts laden. Geöffnet wird
    // der Sammelordner „Schaden" — von dort geht es beliebig tief und auch hoch.
    React.useEffect(() => {
        if (!kontakt) return;
        const ctrl = new AbortController();
        setLaedt(true);
        setFehler(null);
        (async () => {
            try {
                const [wurzel, angelegt, akten] = await Promise.all([
                    documentService.listFolders({ contactId: kontakt.id, prefix: "", category: KUNDENAKTE }, ctrl.signal),
                    kundenakteOrdnerService.liste(kontakt.id, ctrl.signal),
                    fetch(apiUrl("kundenakten-folders", { contactId: String(kontakt.id), limit: 20 }), { signal: ctrl.signal })
                        .then((r) => (r.ok ? r.json() : []))
                        .catch(() => []),
                ]);
                if (ctrl.signal.aborted) return;
                const wurzelNamen = [
                    ...wurzel.map((f) => f.name),
                    ...angelegt.map((p) => p.split("/")[0]).filter(Boolean),
                ];
                setPfad(`${findeSchadenBasis(wurzelNamen)}/`);
                setHatAkte(Array.isArray(akten) && akten.length > 0);
            } catch {
                if (!ctrl.signal.aborted) setFehler("Die Ordner des Kontakts konnten nicht geladen werden.");
            } finally {
                if (!ctrl.signal.aborted) setLaedt(false);
            }
        })();
        return () => ctrl.abort();
    }, [kontakt, ladeTick]);

    const name = ordnerName(neuerName);
    // Ziel: der geöffnete Ordner — oder ein neuer Ordner darin.
    const ziel = `${pfad}${name}`.replace(/\/+$/, "");
    const akteFrageOffen = !!kontakt && hatAkte === false && !akteFrageBeantwortet;
    const kannAblegen = !!kontakt && !!ziel && !busy && !laedt && !akteFrageOffen;

    const ablegen = async () => {
        if (!kontakt || !ziel || !kannAblegen) return;
        setBusy(true);
        setFehler(null);
        try {
            // Hat die Mail Anhänge, vorher fragen, ob sie mitgespeichert werden.
            // Abbrechen = nichts passiert, auch kein neuer Ordner.
            const auswahl = await frageSchadenAnhaenge({ mailbox, messageId, betreff });
            if (!auswahl) {
                setBusy(false);
                return;
            }
            // Der neue Ordner soll auch dann in der Akte stehen, wenn die Ablage
            // der Mail danach scheitert — der Nutzer hat ihn angelegt.
            if (name) await kundenakteOrdnerService.anlegen(kontakt.id, ziel);
            const res = await graphMailService.schadenAblage({ mailbox, messageId, contactId: kontakt.id, ordner: ziel, ...auswahl });
            onAbgelegt({ contactId: kontakt.id, ordner: res.ordner, hinweis: res.hinweis });
            closeWindow(windowId);
        } catch (e) {
            setFehler(e instanceof Error && e.message ? e.message : "Die Mail konnte nicht abgelegt werden.");
            setBusy(false);
        }
    };

    return (
        <div className="kvp-rd" style={wurzel}>
            <div style={inhalt}>
                {/* 1 — Kontakt (Pflicht) */}
                <section>
                    <div style={ueberschrift}>Kontakt</div>
                    {kontakt ? (
                        <div style={chipZeile}>
                            <span style={chip}>{kontakt.name}</span>
                            <button
                                type="button"
                                style={linkKnopf}
                                disabled={busy}
                                onClick={() => {
                                    setKontakt(null);
                                    setHatAkte(null);
                                    setAkteFrageBeantwortet(false);
                                    setPfad("");
                                }}
                            >
                                Anderen Kontakt wählen
                            </button>
                        </div>
                    ) : (
                        <KontaktSuchfeld
                            label="Zu welchem Kontakt gehört der Schaden?"
                            name="schaden-kontakt"
                            placeholder="Name, Firma oder Kundennummer…"
                            value={null}
                            required
                            autoFocus
                            onChange={(t) => t && setKontakt({ id: t.contactId, name: t.displayName })}
                        />
                    )}
                </section>

                {/* 2 — keine Kundenakte: einmal fragen, es geht auch ohne */}
                {akteFrageOffen && !laedt && (
                    <section style={frageBox} role="group" aria-label="Kundenakte zuweisen">
                        <div style={{ fontWeight: 700, color: "#241522" }}>
                            Diesem Kontakt ist keine Kundenakte zugewiesen.
                        </div>
                        <div style={{ color: "#6b5a66", fontSize: 13, marginTop: 4 }}>
                            Soll jetzt eine Kundenakte vom Netzlaufwerk zugewiesen werden? Der
                            Schaden-Ordner lässt sich auch ohne anlegen.
                        </div>
                        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                            <button type="button" style={knopfPrimaer} onClick={() => setAkteZuweisen(true)}>
                                Ja, Akte zuweisen
                            </button>
                            <button type="button" style={knopfSekundaer} onClick={() => setAkteFrageBeantwortet(true)}>
                                Nein, ohne Akte weiter
                            </button>
                        </div>
                    </section>
                )}

                {/* 3 — Ordner der Akte: navigieren, dort ablegen oder neuen Schaden-Ordner anlegen */}
                {kontakt && !akteFrageOffen && (
                    <section>
                        <div style={ueberschrift}>Wohin gehört die Mail?</div>
                        {laedt ? (
                            <div style={hinweis}>Ordner werden geladen…</div>
                        ) : (
                            <>
                                <OrdnerNavigator
                                    contactId={kontakt.id}
                                    pfad={pfad}
                                    onPfad={setPfad}
                                    neuAnlegen
                                    neuLadenKey={ladeTick}
                                    maxHoehe={190}
                                />
                                <div style={{ ...ueberschrift, marginTop: 14 }}>
                                    Neuer Schaden-Ordner in „{ordnerTitel(pfad)}“{" "}
                                    <span style={{ fontWeight: 500, color: "#6b5a66" }}>
                                        {modus === "neu" ? "" : "(leer lassen, um direkt hier abzulegen)"}
                                    </span>
                                </div>
                                <input
                                    value={neuerName}
                                    onChange={(e) => setNeuerName(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && void ablegen()}
                                    placeholder="Name des Schaden-Ordners…"
                                    aria-label="Name des neuen Schaden-Ordners"
                                    maxLength={120}
                                    style={eingabe}
                                />
                            </>
                        )}
                    </section>
                )}

                {fehler && <div role="alert" style={fehlerBox}>{fehler}</div>}
            </div>

            <div style={fuss}>
                <span style={{ flex: 1, color: "#6b5a66", fontSize: 12.5 }}>
                    Hat die Mail Anhänge, wird gefragt, ob sie mitgespeichert werden. Danach wird die Mail archiviert.
                </span>
                <button type="button" style={knopfSekundaer} disabled={busy} onClick={() => closeWindow(windowId)}>
                    Abbrechen
                </button>
                <button
                    type="button"
                    style={{ ...knopfPrimaer, opacity: kannAblegen ? 1 : 0.5, cursor: kannAblegen ? "pointer" : "default" }}
                    disabled={!kannAblegen}
                    onClick={() => void ablegen()}
                >
                    {busy
                        ? "Wird abgelegt…"
                        : name
                          ? "Schaden-Ordner anlegen und ablegen"
                          : `In „${ordnerTitel(pfad)}“ ablegen`}
                </button>
            </div>

            {akteZuweisen && kontakt && (
                <AkteZuweisenPopup
                    contactId={kontakt.id}
                    suchvorschlag={kontakt.name}
                    onClose={() => setAkteZuweisen(false)}
                    onAssigned={() => {
                        setAkteZuweisen(false);
                        setAkteFrageBeantwortet(true);
                        setLadeTick((t) => t + 1);
                    }}
                />
            )}
        </div>
    );
};

const AUB = "#4d2051";
const LINE = "#ece7f0";
const wurzel: React.CSSProperties = { display: "flex", flexDirection: "column", height: "100%", minHeight: 0, fontFamily: "'Barlow', 'Segoe UI', sans-serif", background: "#fff" };
const inhalt: React.CSSProperties = { flex: 1, minHeight: 0, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 18 };
const ueberschrift: React.CSSProperties = { fontWeight: 800, color: "#241522", fontSize: 13.5, marginBottom: 8 };
const chipZeile: React.CSSProperties = { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" };
const chip: React.CSSProperties = { background: "#f9efe3", color: "#241522", borderRadius: 999, padding: "5px 12px", fontWeight: 700, fontSize: 13.5 };
const linkKnopf: React.CSSProperties = { border: "none", background: "transparent", color: AUB, cursor: "pointer", fontSize: 13, fontFamily: "inherit", textDecoration: "underline", padding: 0 };
const frageBox: React.CSSProperties = { border: `1px solid #ecd9b8`, background: "#fdf8ee", borderRadius: 12, padding: 14 };
const hinweis: React.CSSProperties = { color: "#6b5a66", fontSize: 13, padding: "6px 2px" };
const eingabe: React.CSSProperties = { width: "100%", boxSizing: "border-box", padding: "9px 12px", border: `1px solid ${LINE}`, borderRadius: 10, fontSize: 14, fontFamily: "inherit", outline: "none", color: "#241522" };
const fehlerBox: React.CSSProperties = { padding: "8px 12px", borderRadius: 10, background: "#fdf0f0", color: "#8a2626", fontSize: 13 };
const fuss: React.CSSProperties = { display: "flex", alignItems: "center", gap: 8, padding: "12px 16px", borderTop: `1px solid ${LINE}`, flexShrink: 0 };
const knopfSekundaer: React.CSSProperties = { border: `1px solid ${LINE}`, background: "#fff", color: "#241522", borderRadius: 10, padding: "8px 14px", fontSize: 13.5, cursor: "pointer", fontFamily: "inherit" };
const knopfPrimaer: React.CSSProperties = { border: `1px solid ${AUB}`, background: AUB, color: "#fff", borderRadius: 10, padding: "8px 14px", fontSize: 13.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" };

/** Öffnet „Neuer Schaden" bzw. „Einem bestehenden Schaden zuordnen" als Fenster. */
export const openSchadenAblage = (
    openWindow: (config: OpenWindowConfig) => void,
    params: OpenSchadenAblageParams,
): string => {
    const windowId = `schaden-ablage-${Date.now()}`;
    openWindow({
        id: windowId,
        title: params.modus === "neu" ? "Neuer Schaden" : "Einem bestehenden Schaden zuordnen",
        icon: React.createElement(Folder20Regular),
        initialSize: { width: 580, height: 680 },
        center: true,
        render: () => React.createElement(SchadenAblageFenster, { windowId, ...params }),
    });
    return windowId;
};

export default SchadenAblageFenster;
